import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, Download, ShieldAlert } from "lucide-react";
import { cardCls, inputCls, fmtDate, fmtDateTime } from "./ui";

const RFQ_STATUSES = ["NEW", "REVIEWING", "SOURCING", "QUOTED", "NEGOTIATING", "WON", "LOST", "CLOSED"];
const RISK_STATUSES = ["STANDARD", "REVIEW_REQUIRED", "ENHANCED_DUE_DILIGENCE", "RESTRICTED_HOLD", "CLEARED"];
const DOCUMENT_STATUSES = ["NOT_REQUIRED", "REQUESTED", "RECEIVED", "VERIFIED", "REJECTED"];
const STATUS_COLORS = {
  NEW: "text-midnight", REVIEWING: "text-blue-600", SOURCING: "text-indigo-600", QUOTED: "text-gold",
  NEGOTIATING: "text-amber-600", WON: "text-emerald-600", LOST: "text-red-500", CLOSED: "text-midnight/40",
};
const RISK_COLORS = {
  STANDARD: "bg-emerald-50 text-emerald-700",
  REVIEW_REQUIRED: "bg-amber-50 text-amber-700",
  ENHANCED_DUE_DILIGENCE: "bg-indigo-50 text-indigo-700",
  RESTRICTED_HOLD: "bg-red-50 text-red-700",
  CLEARED: "bg-emerald-50 text-emerald-700",
};
const FLAG_LABELS = {
  government: "Government-owned end user", military: "Military / defence-related", nuclear: "Nuclear activities",
  semiconductor: "Advanced semiconductor development", surveillance: "Military / intelligence / surveillance use",
  third_party: "Third party financing / directing", other_country: "Ultimate destination another country",
};

const kv = (obj) => Object.entries(obj || {}).filter(([, v]) => v);

const QualificationBlock = ({ q }) => {
  if (!q) return null;
  const endUser = kv(q.end_user);
  const flags = Object.entries(q.flags || {}).filter(([, v]) => v);
  return (
    <div className="mt-5 border border-indigo-100 bg-indigo-50/40 p-5" data-testid="rfq-qualification">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-700 mb-4 flex items-center gap-2">
        <ShieldAlert size={13} /> End User &amp; Deployment Qualification
      </p>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
        {[["End user status", q.end_user_status], ["Intended use", q.intended_use], ["Use detail", q.intended_use_detail],
          ["Install country", q.install_country], ["Install city", q.install_city], ["Facility", q.facility],
          ["Remains at location", q.remain_at_location], ["Re-export / resale", q.reexport], ["Re-export details", q.reexport_details],
          ["Declaration confirmed", q.declaration ? "Yes" : "No"],
        ].map(([k, v]) => (
          <div key={k}>
            <dt className="text-[10px] font-bold uppercase tracking-wide text-midnight/40">{k}</dt>
            <dd className="text-midnight/80 mt-0.5 break-words">{v || "—"}</dd>
          </div>
        ))}
      </dl>
      {endUser.length > 0 && (
        <div className="mt-4 pt-4 border-t border-indigo-100">
          <p className="text-[10px] font-bold uppercase tracking-wide text-midnight/40 mb-2">End User</p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {endUser.map(([k, v]) => (
              <div key={k}>
                <dt className="text-[10px] font-bold uppercase tracking-wide text-midnight/40">{k}</dt>
                <dd className="text-midnight/80">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {flags.length > 0 && (
        <div className="mt-4 pt-4 border-t border-indigo-100">
          <p className="text-[10px] font-bold uppercase tracking-wide text-red-500 mb-2">Compliance Flags</p>
          {flags.map(([k]) => <p key={k} className="text-sm text-red-600">{FLAG_LABELS[k]}</p>)}
        </div>
      )}
    </div>
  );
};

export const RfqsSection = () => {
  const [rfqs, setRfqs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => api.get("/admin/rfqs").then((r) => setRfqs(r.data.items)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/messages").then((r) => setMessages(r.data.items)).catch(() => {});
  }, []);

  const save = async () => {
    await api.patch(`/admin/rfqs/${selected.id}`, {
      status: selected.status,
      assigned_to: selected.assigned_to || "",
      risk_status: selected.risk_status || "STANDARD",
    });
    setRfqs((r) => r.map((x) => (x.id === selected.id ? selected : x)));
    setSelected(null);
    toast.success("RFQ updated");
  };

  const setDocStatus = async (path, status) => {
    await api.patch(`/admin/rfqs/${selected.id}`, { document_status: { path, status } });
    const docs = (selected.documents || []).map((d) => (d.path === path ? { ...d, status } : d));
    const updated = { ...selected, documents: docs };
    setSelected(updated);
    setRfqs((r) => r.map((x) => (x.id === selected.id ? updated : x)));
    toast.success("Document status updated");
  };

  const documentsOf = (r) =>
    r.documents?.length ? r.documents : r.file_path ? [{ path: r.file_path, name: r.file_name, kind: "po", status: "RECEIVED" }] : [];

  return (
    <div data-testid="admin-rfqs-section">
      <div className={`${cardCls} overflow-x-auto`} data-testid="admin-rfq-table">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3.5 border-b border-midnight/10">
          {["Reference", "Date", "Company", "Product", "Qty", "Risk", "Source", "Status"].map((h, i) => (
            <span key={h} className={`text-[10px] font-bold uppercase tracking-[0.15em] text-midnight/40 ${["col-span-2", "col-span-1", "col-span-2", "col-span-2", "col-span-1", "col-span-2", "col-span-1", "col-span-1"][i]}`}>{h}</span>
          ))}
        </div>
        {rfqs.length === 0 && <p className="px-5 py-10 text-sm text-midnight/40">No requirements received yet.</p>}
        {rfqs.map((r) => (
          <button key={r.id} onClick={() => setSelected(r)} data-testid={`admin-rfq-${r.reference}`} className="w-full text-left grid grid-cols-2 md:grid-cols-12 gap-3 items-center px-5 py-4 border-b border-midnight/[0.06] hover:bg-[#FAFAFA] transition-colors">
            <span className="font-mono text-xs text-gold font-medium md:col-span-2">{r.reference}</span>
            <span className="text-xs text-midnight/45 hidden md:block">{fmtDate(r.created_at)}</span>
            <span className="text-sm text-midnight font-medium md:col-span-2">{r.company_name}</span>
            <span className="text-xs text-midnight/55 md:col-span-2">{r.category || "—"}</span>
            <span className="text-xs text-midnight/55 hidden md:block">{r.quantity || "—"}</span>
            <span className="md:col-span-2">
              <span className={`inline-block text-[10px] font-bold px-2.5 py-1 ${RISK_COLORS[r.risk_status] || RISK_COLORS.STANDARD}`} data-testid={`risk-${r.reference}`}>
                {(r.risk_status || "STANDARD").replace(/_/g, " ")}
              </span>
            </span>
            <span className="text-[10px] font-bold uppercase text-midnight/40">{r.source || "website"}</span>
            <span className={`text-[11px] font-bold tracking-wide ${STATUS_COLORS[r.status] || "text-midnight/45"}`}>{r.status}</span>
          </button>
        ))}
      </div>

      {messages.length > 0 && (
        <div className="mt-8">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-gold mb-4">Contact &amp; AI Handoff Messages</p>
          <div className={cardCls} data-testid="admin-messages-table">
            {messages.map((m) => (
              <div key={m.id} className="px-5 py-4 border-b border-midnight/[0.06]">
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-midnight/45 mb-2">
                  <span className="font-semibold text-midnight">{m.name}</span><span>{m.email}</span><span>{m.company}</span><span>{fmtDateTime(m.created_at)}</span>
                </div>
                <p className="text-sm text-midnight/70 whitespace-pre-wrap">{m.message.length > 600 ? m.message.slice(0, 600) + "…" : m.message}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-midnight/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="rfq-detail-modal">
          <div className="bg-white hairline shadow-medium max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="font-mono text-xs text-gold font-medium">{selected.reference}</p>
                <h3 className="font-heading text-xl text-midnight mt-1">{selected.company_name}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="text-midnight/40 hover:text-midnight" aria-label="Close"><X size={18} /></button>
            </div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["Contact", selected.contact_name], ["Email", selected.email], ["Phone", selected.phone], ["Website", selected.website],
                ["Country", selected.country], ["Category", selected.category], ["Part Number", selected.part_number], ["Quantity", selected.quantity],
                ["Target Price", selected.target_price && `${selected.target_price} ${selected.currency || ""}`], ["Delivery", selected.delivery_date],
                ["Destination", selected.destination], ["Incoterm", selected.incoterm], ["Source", selected.source || "website"],
                ["NDA", selected.confidentiality ? "Requested" : "—"], ["Submitted", fmtDateTime(selected.created_at)],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[10px] font-bold uppercase tracking-wide text-midnight/40">{k}</dt>
                  <dd className="text-midnight/80 mt-0.5 break-words">{v || "—"}</dd>
                </div>
              ))}
            </dl>
            {selected.description && <p className="mt-5 text-sm text-midnight/65 border-t border-midnight/10 pt-4">{selected.description}</p>}
            {selected.notes && <p className="mt-2 text-sm text-midnight/50">{selected.notes}</p>}

            <QualificationBlock q={selected.qualification} />

            {documentsOf(selected).length > 0 && (
              <div className="mt-5" data-testid="rfq-documents">
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-midnight/40 mb-3">Documents</p>
                {documentsOf(selected).map((d) => (
                  <div key={d.path} className="flex flex-wrap items-center gap-3 py-2.5 border-b border-midnight/[0.06]">
                    <span className="text-[10px] font-bold uppercase text-midnight/40 w-20">{d.kind}</span>
                    <a href={`${api.defaults.baseURL}/admin/rfqs/${selected.id}/file`} className="flex items-center gap-1.5 text-xs font-medium text-midnight hover:text-gold">
                      <Download size={12} /> {d.name}
                    </a>
                    <select
                      value={d.status}
                      onChange={(e) => setDocStatus(d.path, e.target.value)}
                      data-testid={`doc-status-${d.name}`}
                      className="ml-auto bg-white border border-midnight/15 px-2 py-1.5 text-[11px] font-medium text-midnight outline-none"
                    >
                      {DOCUMENT_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-3 gap-4 mt-6 pt-5 border-t border-midnight/10">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.12em] text-midnight/50 mb-1.5">Status</label>
                <select value={selected.status} onChange={(e) => setSelected((s) => ({ ...s, status: e.target.value }))} data-testid="rfq-status-select" className={inputCls}>
                  {RFQ_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.12em] text-midnight/50 mb-1.5">Risk (internal)</label>
                <select value={selected.risk_status || "STANDARD"} onChange={(e) => setSelected((s) => ({ ...s, risk_status: e.target.value }))} data-testid="rfq-risk-select" className={inputCls}>
                  {RISK_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.12em] text-midnight/50 mb-1.5">Assigned To</label>
                <input value={selected.assigned_to || ""} onChange={(e) => setSelected((s) => ({ ...s, assigned_to: e.target.value }))} data-testid="rfq-assigned" className={inputCls} placeholder="Team member" />
              </div>
            </div>
            <button onClick={save} data-testid="rfq-save" className="mt-5 bg-midnight text-white text-sm font-semibold px-8 py-3 hover:bg-midnight-light transition-colors">Save</button>
          </div>
        </div>
      )}
    </div>
  );
};
